import { useState } from 'react';
import { Confirm } from './../../Commons/Modals/Confirm'
import { MESSAGE_TYPE, useFloatMessage } from './../../Providers/FloatMessage'
import { SecondaryBase } from './../Commons/SecondaryBase'
import { ConfigGame as BLConfigGame, ResetGame as BLResetGame, ShowOverlay as BLShowOverlay, HideOverlay as BLHideOverlay, RefreshOverlay as BLRefreshOverlay, ConfigOverlay as BLConfigOverlay } from './../../../utilities/BL/Admin/Games/PluckingRooster'


const PluckingRooster = ({ getOutOfTheDynamic }) => {
    const { showMessage } = useFloatMessage();

    // --- ESTADOS ---
    const [duration, setDuration] = useState(15);
    const [overlayConfig, setOverlayConfig] = useState({
        layoutMode: 1, // 1: Vertical, 2: Horizontal
        positionOffset: 50
    });
    const [showConfirmReset, setShowConfirmReset] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    // --- ACCIONES DE NAVEGACIÓN ---
    const ReturnToDashboard = async () => {
        try { getOutOfTheDynamic(); } catch { getOutOfTheDynamic(); }
    };

    // --- ACCIONES DEL JUEGO ---
    const handleConfigGame = async () => {
        setIsLoading(true);
        const result = await BLConfigGame(duration);
        setIsLoading(false);

        if (result.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Duración actualizada' });
        else showMessage({ type: MESSAGE_TYPE.ERROR, text: result.message });
    };

    const handleResetGame = async () => {
        setIsLoading(true);
        const result = await BLResetGame();
        setIsLoading(false);
        setShowConfirmReset(false);

        if (result.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Juego reiniciado. Base de datos limpia.' });
        else showMessage({ type: MESSAGE_TYPE.ERROR, text: result.message });
    };

    // --- ACCIONES DEL OVERLAY ---
    const handleShowOverlay = async () => {
        const result = await BLShowOverlay();
        if (result.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Ranking visible en pantalla' });
    };

    const handleHideOverlay = async () => {
        const result = await BLHideOverlay();
        if (result.success) showMessage({ type: MESSAGE_TYPE.INFO, text: 'Ranking oculto' });
    };

    const handleRefreshOverlay = async () => {
        const result = await BLRefreshOverlay();
        if (result.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Datos actualizados en pantalla' });
    };

    const handleConfigOverlay = async () => {
        const result = await BLConfigOverlay(overlayConfig.layoutMode, overlayConfig.positionOffset);
        if (result.success) showMessage({ type: MESSAGE_TYPE.SUCCESS, text: 'Diseño actualizado' });
    };

    return (
        <SecondaryBase actionToExecute={ReturnToDashboard}>
            <div className="flex flex-col gap-6 p-4 w-full max-w-4xl mx-auto">
                
                <div className="flex justify-between items-center border-b pb-4">
                    <h1 className="text-2xl font-bold text-gray-800">Desplumando al Gallo</h1>
                    <span className="bg-orange-100 text-orange-800 px-3 py-1 rounded-full text-sm font-medium">
                        Panel de Control
                    </span>
                </div>

                {/* --- SECCIÓN 1: CONTROL DE PANTALLA GIGANTE --- */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold mb-4 text-blue-900 flex items-center gap-2">Pantalla Gigante (Overlay)</h2>
                    
                    {/* Controles de Visibilidad */}
                    <div className="grid grid-cols-3 gap-4 mb-6">
                        <button onClick={handleShowOverlay} className="Btn__Adsl--RedOutlineTransparent">
                            MOSTRAR RANKING
                        </button>
                        <button onClick={handleRefreshOverlay} className="Btn__Adsl--RedOutlineTransparent">
                            ACTUALIZAR DATOS
                        </button>
                        <button onClick={handleHideOverlay} className="Btn__Adsl--RedOutlineTransparent">
                            OCULTAR
                        </button>
                    </div>

                    {/* Configuración de Diseño */}
                    <div className="bg-gray-50 p-4 rounded border border-gray-200">
                        <h3 className="text-sm font-bold text-gray-500 uppercase mb-3">Configuración Visual</h3>
                        <div className="flex flex-wrap gap-4 items-end">
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Orientación</label>
                                <select 
                                    className="p-2 border rounded w-40"
                                    value={overlayConfig.layoutMode}
                                    onChange={(e) => setOverlayConfig({...overlayConfig, layoutMode: parseInt(e.target.value)})}
                                >
                                    <option value={1}>Vertical (Sidebar)</option>
                                    <option value={2}>Horizontal (Cintillo)</option>
                                </select>
                            </div>
                            <div>
                                <label className="block text-sm text-gray-700 mb-1">Posición (Offset px)</label>
                                <input 
                                    type="number" 
                                    className="p-2 border rounded w-24"
                                    value={overlayConfig.positionOffset}
                                    onChange={(e) => setOverlayConfig({...overlayConfig, positionOffset: parseInt(e.target.value)})}
                                />
                            </div>
                            <button onClick={handleConfigOverlay} className="Btn__Adsl--RedOutlineTransparent">
                                Aplicar Diseño
                            </button>
                        </div>
                    </div>
                </div>

                {/* --- SECCIÓN 2: CONFIGURACIÓN DEL JUEGO --- */}
                <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                    <h2 className="text-lg font-semibold mb-4 text-orange-900 flex items-center gap-2">Configuración del Juego</h2>
                    
                    <div className="flex items-end gap-4 mb-6">
                        <div className="flex-1">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Duración (Segundos)</label>
                            <input 
                                type="number" 
                                className="w-full p-2 border rounded focus:ring-2 focus:ring-orange-500 outline-none"
                                value={duration}
                                onChange={(e) => setDuration(parseInt(e.target.value))}
                            />
                        </div>
                        <button 
                            onClick={handleConfigGame}
                            className="Btn__Adsl--RedOutlineTransparent"
                        >
                            Guardar Tiempo
                        </button>
                    </div>

                    <div className="border-t pt-4">
                        <h3 className="text-sm font-bold text-red-600 uppercase mb-2">Zona de Peligro</h3>
                        <p className="text-sm text-gray-500 mb-3">Esto borrará todos los jugadores y puntuaciones de la base de datos.</p>
                        <button 
                            onClick={() => setShowConfirmReset(true)} 
                            className="Btn__Adsl--RedOutlineTransparent"
                        >
                            RESETEAR JUEGO Y RANKING
                        </button>
                    </div>
                </div>
            </div>

            {/* MODAL DE CONFIRMACIÓN */}
            {showConfirmReset && (
                <Confirm 
                    open={showConfirmReset}
                    title="¿Estás seguro?"
                    message="Esta acción eliminará TODOS los registros de jugadores y puntuaciones. No se puede deshacer."
                    onConfirm={handleResetGame}
                    onCancel={() => setShowConfirmReset(false)}
                />
            )}
        </SecondaryBase>
    );
};

export { PluckingRooster };
















// const PluckingRooster = ({ getOutOfTheDynamic }) => {

//     const ReturnToDashboard = async () => {
//         try {
//             getOutOfTheDynamic()
//         }
//         catch
//         {
//             getOutOfTheDynamic()
//         }
//     }


//     return (
//         <SecondaryBase actionToExecute={ReturnToDashboard}>
//             <p>Desplumando al Gallo</p>
//         </SecondaryBase>
//     )
// }

// export { PluckingRooster };